import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, withConnectionCheck } from '../lib/supabase';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import {
  Eye,
  ArrowLeft,
  Loader2,
  Package,
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  MapPin,
  User,
  FileText,
  Calendar,
  DollarSign,
  Save,
  Printer,
  Download,
  Share2,
  Building,
  Phone,
  Mail,
  Hash,
  FileSpreadsheet,
  ShoppingCart,
  UserCheck,
  MessageSquare,
  History,
  Send,
  Flag,
} from 'lucide-react';
import { sendOrderStatusUpdateEmail, sendOrderConfirmationEmail, EmailResponse } from '../lib/emailService';
import { ProxyImage } from '../lib/imageProxy';
import { 
  cancelOrder, 
  canCancelOrder, 
  canRetryPayment, 
  getPaymentStatusMessage,
  getPaymentButtonStyle,
  getPaymentButtonText 
} from '../lib/orderUtils';


interface OrderItem {
  product_id: string;
  quantity: number;
  price: number;
  product: {
    name: string;
    main_image: string;
    stock?: number;
    code?: string;
  };
  dispatch_quantity?: number;
}

interface OrderHistoryEntry {
  id: string;
  order_id: string;
  previous_status: string | null;
  new_status: string;
  changed_by: string | null;
  changed_by_name?: string;
  action: string;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface Employee {
  id: string;
  name: string;
  email?: string;
}

interface Order {
  id: string;
  created_at: string;
  total_amount: number;
  shipping_cost: number;
  status: 'pending' | 'pending_payment' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  payment_method: 'card' | 'cash' | 'twoCards' | 'bankTransfer';
  delivery_method: 'delivery' | 'pickup';
  priority?: 'normal' | 'high' | 'urgent';
  assigned_to?: string;
  assigned_to_name?: string;
  internal_notes?: string;
  shipping_address?: {
    first_name: string;
    last_name: string;
    company: string;
    nit: string;
    nit_type: 'NIT' | 'CF';
    address: string;
    address_details?: string;
    city: string;
    region: string;
    postal_code?: string;
    phone: string;
    email: string;
  };
  customer_name?: string;
  customer_email?: string;
  transfer_receipt_url?: string;
  user: {
    email: string;
    profile: {
      name: string;
      company: string;
      phone: string;
    };
  };
  items: OrderItem[];
}

// Estados simplificados del nuevo sistema
const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  pending_payment: 'bg-orange-100 text-orange-800 border-orange-200',
  processing: 'bg-blue-100 text-blue-800 border-blue-200',
  shipped: 'bg-purple-100 text-purple-800 border-purple-200',
  delivered: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const paymentStatusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  paid: 'bg-green-100 text-green-800 border-green-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  refunded: 'bg-gray-100 text-gray-800 border-gray-200'
};

const statusLabels: Record<string, string> = {
  pending: 'Nuevo',
  pending_payment: 'Verificando Pago',
  processing: 'Preparando',
  shipped: 'Enviado',
  delivered: 'Entregado',
  cancelled: 'Cancelado'
};

const paymentStatusLabels: Record<string, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  failed: 'Fallido',
  refunded: 'Reembolsado'
};

const statusIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  pending: Clock,
  pending_payment: CreditCard,
  processing: Package,
  shipped: Truck,
  delivered: CheckCircle,
  cancelled: XCircle
};

const priorityColors: Record<string, string> = {
  normal: 'bg-gray-100 text-gray-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700'
};

const priorityLabels: Record<string, string> = {
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente'
};

// Mapeo de acciones del historial
const actionLabels: Record<string, string> = {
  status_change: 'Cambio de estado',
  created: 'Pedido creado',
  assigned: 'Asignado',
  note_added: 'Nota agregada',
  payment_verified: 'Pago verificado',
  cancelled: 'Cancelado'
};

interface ViewOrderProps {
  isClientView?: boolean;
}

export default function ViewOrder({ isClientView = false }: ViewOrderProps) {
  const { orderId, id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);

  // Nuevos estados para gestión de pedidos
  const [orderHistory, setOrderHistory] = useState<OrderHistoryEntry[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [showHistoryPanel, setShowHistoryPanel] = useState(true);

  // Usar orderId (ruta del perfil) o id (ruta del admin) según lo que esté disponible
  const currentOrderId = orderId || id;

  // Función de verificación de permisos de administrador
  const verifyAdminPermission = async (permission: string): Promise<boolean> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: profile } = await supabase
      .from('profiles')
      .select('is_admin, admin_permissions')
      .eq('id', user.id)
      .single();

    if (!profile) return false;
    if (profile.is_admin) return true;

    return profile.admin_permissions?.[permission] === true;
  };

  useEffect(() => {
    fetchOrder();
    if (!isClientView) {
      fetchOrderHistory();
      fetchEmployees();
    }
  }, [currentOrderId, isClientView]);

  useEffect(() => {
    if (order) {
      setOrderItems(order.items.map(item => ({
        ...item,
        dispatch_quantity: item.quantity
      })));
      if (order.assigned_to) {
        setSelectedEmployee(order.assigned_to);
      }
    }
  }, [order]);

  // Obtener historial de la orden
  const fetchOrderHistory = async () => {
    if (!currentOrderId) return;

    try {
      const { data, error } = await supabase
        .from('order_history')
        .select(`
          *,
          changed_by_profile:profiles!order_history_changed_by_fkey(name)
        `)
        .eq('order_id', currentOrderId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching order history:', error);
        return;
      }

      const historyWithNames = (data || []).map(entry => ({
        ...entry,
        changed_by_name: entry.changed_by_profile?.name || 'Sistema'
      }));

      setOrderHistory(historyWithNames);
    } catch (err) {
      console.error('Error fetching order history:', err);
    }
  };

  // Obtener lista de empleados (admins)
  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name, email')
        .eq('is_admin', true)
        .order('name');

      if (error) {
        console.error('Error fetching employees:', error);
        return;
      }

      setEmployees(data || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  // Asignar pedido a empleado
  const handleAssignOrder = async (employeeId: string) => {
    if (!currentOrderId) return;

    try {
      setIsAssigning(true);

      const hasPermission = await verifyAdminPermission('orders');
      if (!hasPermission) {
        alert('No tienes permisos para asignar órdenes');
        return;
      }

      const { error } = await supabase.rpc('assign_order_to_employee', {
        p_order_id: currentOrderId,
        p_employee_id: employeeId || null
      });

      if (error) throw error;

      setSelectedEmployee(employeeId);
      await fetchOrder();
      await fetchOrderHistory();

      alert(employeeId ? 'Pedido asignado correctamente' : 'Asignación removida');
    } catch (err) {
      console.error('Error assigning order:', err);
      alert('Error al asignar el pedido');
    } finally {
      setIsAssigning(false);
    }
  };

  // Agregar nota al pedido
  const handleAddNote = async () => {
    if (!currentOrderId || !newNote.trim()) return;

    try {
      setIsAddingNote(true);

      const hasPermission = await verifyAdminPermission('orders');
      if (!hasPermission) {
        alert('No tienes permisos para agregar notas');
        return;
      }

      const { error } = await supabase.rpc('add_order_note', {
        p_order_id: currentOrderId,
        p_note: newNote.trim()
      });

      if (error) throw error;

      setNewNote('');
      await fetchOrder();
      await fetchOrderHistory();

      alert('Nota agregada correctamente');
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Error al agregar la nota');
    } finally {
      setIsAddingNote(false);
    }
  };

  // Actualizar prioridad
  const handlePriorityChange = async (priority: 'normal' | 'high' | 'urgent') => {
    if (!currentOrderId) return;

    try {
      const hasPermission = await verifyAdminPermission('orders');
      if (!hasPermission) {
        alert('No tienes permisos para cambiar la prioridad');
        return;
      }

      const { error } = await supabase
        .from('orders')
        .update({ priority })
        .eq('id', currentOrderId);

      if (error) throw error;

      await fetchOrder();
    } catch (err) {
      console.error('Error updating priority:', err);
      alert('Error al actualizar la prioridad');
    }
  };

  const handleDispatchQuantityChange = (productId: string, quantity: number) => {
    setOrderItems(prevItems => 
      prevItems.map(item => 
        item.product_id === productId 
          ? { ...item, dispatch_quantity: Math.min(quantity, item.product.stock || 0) }
          : item
      )
    );
  };

  const calculateDispatchTotal = () => {
    return orderItems.reduce((total, item) => {
      const dispatchQty = item.dispatch_quantity || 0;
      return total + (dispatchQty * item.price);
    }, 0);
  };

  const handleOrderConfirmation = async () => {
    try {
      setIsConfirming(true);

      // Verificar permisos primero
      const hasPermission = await verifyAdminPermission('orders');
      if (!hasPermission) {
        setError('No tienes permisos para confirmar órdenes');
        alert('No tienes permisos para confirmar órdenes');
        setIsConfirming(false);
        return;
      }

      // Verificar que todas las cantidades sean válidas
      const allQuantitiesValid = orderItems.every(item => {
        const dispatchQty = item.dispatch_quantity || 0;
        return dispatchQty > 0 && dispatchQty <= (item.product.stock || 0);
      });

      if (!allQuantitiesValid) {
        alert('Por favor verifica las cantidades a despachar');
        setIsConfirming(false);
        return;
      }

      console.log('Verificando conexión antes de despachar el pedido...');

      // Usar withConnectionCheck para verificar la conexión
      const result = await withConnectionCheck(async () => {
        // Intentar calcular el total en el servidor para mayor seguridad
        const { data: serverTotal, error: totalError } = await supabase
          .rpc('calculate_order_total', { p_order_id: currentOrderId });

        // Si la función RPC existe y funciona, usar el total del servidor
        // Si no, usar el cálculo local como fallback
        let totalAmount: number;
        if (totalError || serverTotal === null || serverTotal === undefined) {
          console.warn('Usando cálculo local del total (RPC no disponible):', totalError?.message);
          totalAmount = calculateDispatchTotal();
        } else {
          totalAmount = serverTotal;
          console.log('Usando total calculado en servidor:', totalAmount);
        }
        
        // Actualizar el estado del pedido a "pending_payment" y el total
        const { error: updateError } = await supabase
          .from('orders')
          .update({
            status: 'pending_payment' as Order['status'],
            total_amount: totalAmount
          })
          .eq('id', currentOrderId);

        if (updateError) {
          console.error('Error al actualizar el pedido:', updateError);
          throw new Error(`Error al actualizar el pedido: ${updateError.message}`);
        }

        console.log('Pedido actualizado correctamente');
        console.log('Actualizando items del pedido...');

        // Primero eliminar los items existentes para evitar duplicados
        const { error: deleteError } = await supabase
          .from('order_items')
          .delete()
          .eq('order_id', currentOrderId);
          
        if (deleteError) {
          console.error('Error al eliminar items existentes:', deleteError);
          throw new Error(`Error al eliminar items existentes: ${deleteError.message}`);
        }

        // Preparar los datos de los items para actualizar
        const itemsToUpdate = orderItems.map(item => ({
          order_id: currentOrderId,
          product_id: item.product_id,
          quantity: item.dispatch_quantity || 0,
          price: item.price
        }));

        console.log('Items a actualizar:', itemsToUpdate);

        // Insertar los nuevos items uno por uno
        for (const item of itemsToUpdate) {
          console.log('Insertando item:', item);
          const { error } = await supabase
            .from('order_items')
            .insert(item)
            .select();

          if (error) {
            console.error('Error al insertar item:', error, item);
            throw new Error(`Error al insertar item: ${error.message}`);
          }
        }

        console.log('Items actualizados correctamente');
        
        return totalAmount;
      }, () => {
        // Esta función se ejecuta cuando no hay conexión
        alert('No hay conexión a internet. Verifica tu conexión y vuelve a intentarlo.');
        return undefined;
      });
      
      if (result === undefined) {
        // Si no hay conexión, terminamos aquí
        return;
      }
      
      const totalAmount = result;
      
      // Actualizar la orden en la UI
      await fetchOrder();
      
      // Enviar correo de confirmación al cliente
      if (order) {
        const orderItemsForEmail = orderItems.map(item => ({
          name: item.product.name,
          quantity: item.dispatch_quantity || 0,
          price: item.price
        }));
        
        const formattedDate = format(new Date(order.created_at), "d 'de' MMMM, yyyy", { locale: es });
        
        // Verificar que el email sea válido
        if (!order.user.email || order.user.email === 'No disponible') {
          console.warn('No se encontró una dirección de correo válida para enviar la confirmación');
          alert('No se pudo enviar el correo de confirmación porque no se encontró una dirección de correo válida.');
        } else {
          console.log('Intentando enviar correo a:', order.user.email);
          
          try {
            // Utiliza withConnectionCheck para verificar la conexión antes de enviar el correo
            const emailResult = await withConnectionCheck<EmailResponse>(async () => {
              return await sendOrderConfirmationEmail({
                orderId: order.id,
                customerName: order.shipping_address?.first_name || order.user.profile.name,
                customerEmail: order.user.email,
                totalAmount,
                orderDate: formattedDate,
                items: orderItemsForEmail,
                deliveryMethod: order.delivery_method,
                ...(order.delivery_method === 'delivery' && order.shipping_address && {
                  shippingAddress: {
                    address: order.shipping_address.address,
                    city: order.shipping_address.city,
                    region: order.shipping_address.region
                  }
                })
              });
            }, () => {
              // Función que se ejecuta cuando no hay conexión
              console.error('No hay conexión a internet para enviar el correo');
              alert('No se pudo enviar el correo de confirmación porque no hay conexión a internet.');
            });
            
            // Verificar si emailResult está definido
            if (emailResult) {
              if (!emailResult.success && emailResult.error) {
                console.error('Error al enviar el correo de confirmación:', emailResult.error);
                
                // En modo desarrollo, no bloquear el flujo por errores de correo
                if (import.meta.env.DEV) {
                  console.warn('Continuando a pesar del error de correo (modo desarrollo)');
                } else {
                  // En producción, mostrar mensaje informativo pero continuar con el flujo
                  console.warn('Ocurrió un error al enviar el correo, pero el pedido se procesó correctamente');
                  alert('El pedido se procesó correctamente, pero hubo un problema al enviar el correo de confirmación. El cliente recibirá el correo más tarde cuando el sistema lo reintente automáticamente.');
                }
              } else if (emailResult.success) {
                console.log('Correo de confirmación enviado correctamente');
              }
            } else {
              console.warn('No se pudo verificar la conexión o hubo un error en la verificación');
            }
          } catch (emailSendError) {
            console.error('Error al intentar enviar el correo:', emailSendError);
            
            // En modo desarrollo, no bloquear el flujo por errores de correo
            if (import.meta.env.DEV) {
              console.warn('Continuando a pesar del error de correo (modo desarrollo)');
            } else {
              // En producción, mostrar mensaje informativo pero continuar con el flujo
              console.warn('Ocurrió un error al enviar el correo, pero el pedido se procesó correctamente');
              alert('El pedido se procesó correctamente, pero hubo un problema al enviar el correo de confirmación. El cliente recibirá el correo más tarde cuando el sistema lo reintente automáticamente.');
            }
          }
        }
      }
      
      alert('Pedido despachado exitosamente. El cliente ahora puede proceder al pago.');
    } catch (err) {
      console.error('Error completo al despachar el pedido:', err);
      
      if (err instanceof Error) {
        setError(`Error al despachar el pedido: ${err.message}`);
        alert(`Error al despachar el pedido: ${err.message}`);
      } else {
        setError('Error desconocido al despachar el pedido');
        alert('Error desconocido al despachar el pedido. Verifica tu conexión a internet.');
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const handlePaymentRedirect = () => {
    if (currentOrderId) {
      navigate(`/order/${currentOrderId}/payment`);
    }
  };

  // Función para confirmar pago de transferencia manualmente
  const handleConfirmPayment = async () => {
    if (!order || !currentOrderId) return;

    const confirmed = window.confirm(
      '¿Confirmar que el pago ha sido verificado? Esto cambiará el estado del pedido a "Preparando".'
    );

    if (!confirmed) return;

    try {
      setIsConfirmingPayment(true);

      // Verificar permisos
      const hasPermission = await verifyAdminPermission('orders');
      if (!hasPermission) {
        alert('No tienes permisos para confirmar pagos');
        return;
      }

      // Actualizar payment_status a 'paid' y status a 'processing'
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          payment_status: 'paid',
          status: 'processing'
        })
        .eq('id', currentOrderId);

      if (updateError) throw updateError;

      // Recargar la orden
      await fetchOrder();

      // Enviar email de confirmación de pago al cliente usando la cola de emails
      const customerEmail = order.user.email && order.user.email !== 'No disponible'
        ? order.user.email
        : order.shipping_address?.email;

      if (customerEmail) {
        try {
          const customerName = order.shipping_address?.first_name || order.user.profile.name || 'Cliente';
          const orderUrl = `https://disolgua.com/order/${currentOrderId}`;

          // Preparar items para el email
          const emailItems = orderItems.map(item => ({
            name: item.product_name || 'Producto',
            quantity: item.quantity,
            price: item.unit_price,
            subtotal: item.quantity * item.unit_price,
            image: item.product_image || null
          }));

          // Encolar email de pago confirmado al cliente
          await supabase.from('email_queue').insert({
            type: 'payment_confirmed',
            recipient: customerEmail,
            subject: `Pago verificado - Pedido #${currentOrderId}`,
            data: {
              orderId: currentOrderId,
              customerName,
              totalAmount: order.total,
              shippingCost: order.shipping_cost || 0,
              items: emailItems,
              orderDate: new Date(order.created_at).toLocaleDateString('es-GT', { year: 'numeric', month: 'long', day: 'numeric' }),
              orderUrl
            },
            status: 'pending'
          });
          // Invocar process-email-queue para enviar el email inmediatamente
          try {
            await supabase.functions.invoke('process-email-queue');
          } catch (invokeError) {
            console.error('Error al invocar process-email-queue:', invokeError);
          }
        } catch (emailError) {
          console.error('Error al encolar email de confirmación de pago:', emailError);
        }
      }

      alert('Pago confirmado exitosamente. El pedido ahora está en estado "Preparando".');
    } catch (err) {
      console.error('Error al confirmar pago:', err);
      alert('Error al confirmar el pago. Inténtalo de nuevo.');
    } finally {
      setIsConfirmingPayment(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!currentOrderId) return;
    
    const confirmed = window.confirm(
      '¿Estás seguro de que deseas cancelar este pedido? Esta acción no se puede deshacer.'
    );
    
    if (!confirmed) return;

    try {
      setIsUpdating(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      const result = await cancelOrder(currentOrderId, isClientView ? user?.id : undefined);

      if (result.success) {
        await fetchOrder();
        alert(result.message);
      } else {
        alert(result.message);
      }
      
    } catch (err) {
      console.error('Error cancelling order:', err);
      alert('Error al cancelar el pedido.');
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      // Consulta básica para obtener el pedido
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          items:order_items (
            *,
            product:products (*)
          )
        `)
        .eq('id', currentOrderId)
        .single();

      if (ordersError) {
        console.error('Error al obtener pedido:', ordersError);
        throw ordersError;
      }

      if (!ordersData) {
        throw new Error('No se encontró el pedido');
      }

      // Obtener el perfil del usuario del pedido
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', ordersData.user_id)
        .single();

      if (profileError) {
        console.error('Error al obtener perfil:', profileError);
        throw profileError;
      }
      
      // Determinar el email a utilizar
      let userEmail = '';
      
      // Si el pedido tiene un campo email, usarlo directamente
      if (ordersData.email) {
        userEmail = ordersData.email;
        console.log('Usando email almacenado en la orden:', userEmail);
      }
      // Si el pedido pertenece al usuario actual, usar su email
      else if (ordersData.user_id === user.id) {
        userEmail = user.email || '';
        console.log('Usando email del usuario actual:', userEmail);
      } else {
        // Si sigue sin haber email, usar valor por defecto
        userEmail = profileData.email || 'No disponible';
        console.warn('Usando email del perfil o valor por defecto');
      }

      console.log('Email final del cliente para este pedido:', userEmail);
      // console.log('🔍 Datos de la orden cargados:', ordersData.id, ordersData.payment_method);

      // Obtener nombre del empleado asignado si existe
      let assignedToName = '';
      if (ordersData.assigned_to) {
        const { data: assignedProfile } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', ordersData.assigned_to)
          .single();
        assignedToName = assignedProfile?.name || '';
      }

      // Transformar los datos
      const transformedOrder: Order = {
        id: ordersData.id,
        created_at: ordersData.created_at,
        total_amount: ordersData.total_amount,
        shipping_cost: ordersData.shipping_cost || 0,
        status: ordersData.status,
        payment_status: ordersData.payment_status,
        payment_method: ordersData.payment_method || 'card',
        delivery_method: ordersData.delivery_method || 'delivery',
        priority: ordersData.priority || 'normal',
        assigned_to: ordersData.assigned_to || undefined,
        assigned_to_name: assignedToName || undefined,
        internal_notes: ordersData.internal_notes || undefined,
        shipping_address: ordersData.shipping_address ?
          (typeof ordersData.shipping_address === 'string'
            ? (() => {
                try {
                  return JSON.parse(ordersData.shipping_address);
                } catch (e) {
                  console.error('Error parsing shipping_address:', e, ordersData.shipping_address);
                  return null;
                }
              })()
            : ordersData.shipping_address)
          : null,
        transfer_receipt_url: ordersData.transfer_receipt_url,
        user: {
          email: userEmail,
          profile: {
            name: profileData.name || 'No disponible',
            company: profileData.company || '',
            phone: profileData.phone || ''
          }
        },
        items: ordersData.items.map((item: any) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
          product: {
            name: item.product?.name || 'Producto no disponible',
            main_image: item.product?.main_image || '',
            stock: item.product?.stock || 0,
            code: item.product?.code || item.product?.sku || ''
          }
        }))
      };

      setOrder(transformedOrder);
    } catch (err) {
      console.error('Error al cargar el pedido:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar el pedido');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (status: Order['status']) => {
    try {
      setIsUpdating(true);

      // Verificar permisos primero
      const hasPermission = await verifyAdminPermission('orders');
      if (!hasPermission) {
        setError('No tienes permisos para actualizar órdenes');
        alert('No tienes permisos para actualizar órdenes');
        setIsUpdating(false);
        return;
      }

      // Usar withConnectionCheck para verificar la conexión
      const success = await withConnectionCheck(async () => {
        const { error } = await supabase
          .from('orders')
          .update({ status })
          .eq('id', currentOrderId);

        if (error) {
          throw error;
        }
        
        await fetchOrder();
        return true;
      }, () => {
        // Esta función se ejecuta cuando no hay conexión
        alert('No hay conexión a internet. Verifica tu conexión y vuelve a intentarlo.');
        return false;
      });
      
      if (!success) {
        // Si no hay conexión o hubo un error, terminamos aquí
        setIsUpdating(false);
        return;
      }
      
      // Si el email está disponible, enviar notificación de actualización
      if (order?.user.email && order.user.email !== 'No disponible') {
        try {
          console.log('Enviando correo de actualización de estado...');
          
          // Obtener mensajes según el status
          const statusMessages: Record<Order['status'], string> = {
            pending: 'Tu pedido ha sido registrado y está pendiente de confirmación.',
            confirmed: 'Tu pedido ha sido confirmado y está siendo preparado.',
            pending_payment: 'Tu pedido está pendiente de pago.',
            paid: 'Tu pago ha sido recibido correctamente.',
            processing: 'Tu pedido está siendo procesado.',
            shipped: 'Tu pedido ha sido enviado.',
            delivered: 'Tu pedido ha sido entregado.',
            cancelled: 'Tu pedido ha sido cancelado.'
          };
          
          const statusMessage = statusMessages[status] || `Tu pedido ha sido actualizado al estado: ${status}`;
          
          // Verificar la conexión antes de enviar el correo
          const emailResult = await withConnectionCheck<EmailResponse>(async () => {
            // Enviar correo de notificación
            return await sendOrderStatusUpdateEmail({
              orderId: currentOrderId || '',
              customerName: order.shipping_address?.first_name || order.user.profile.name || 'Cliente',
              customerEmail: order.user.email || '',
              newStatus: status,
              statusMessage: statusMessage,
            });
          }, () => {
            // Función que se ejecuta cuando no hay conexión
            console.error('No hay conexión a internet para enviar el correo de actualización');
            alert('No se pudo enviar la notificación por correo porque no hay conexión a internet.');
          });
          
          // Verificar si emailResult está definido
          if (emailResult) {
            if (!emailResult.success) {
              console.error('Error al enviar correo:', emailResult.error);
              alert(`Error al enviar correo de notificación: ${emailResult.error}`);
            } else {
              alert('Estado del pedido actualizado y notificación enviada');
            }
          } else {
            console.warn('No se pudo verificar la conexión o hubo un error en la verificación');
            alert('Estado del pedido actualizado, pero no se pudo enviar la notificación');
          }
        } catch (error) {
          console.error('Error al enviar correo de notificación:', error);
          alert('Error al enviar correo de notificación');
        }
      } else {
        // No hay email disponible
        alert('Estado del pedido actualizado. No se envió notificación por correo porque no hay dirección de email registrada.');
      }
      
      setIsUpdating(false);
    } catch (error) {
      console.error('Error al actualizar estado del pedido:', error);
      alert('Error al actualizar estado');
      setIsUpdating(false);
    }
  };

  const handlePrint = async () => {
    if (!invoiceRef.current) return;
    
    try {
      setIsGeneratingPDF(true);
      
      // Crear una copia del elemento para modificarlo sin afectar la UI
      const element = invoiceRef.current.cloneNode(true) as HTMLElement;
      document.body.appendChild(element);
      
      // Aplicar estilos específicos para la impresión
      element.style.width = '100%';
      element.style.padding = '0';
      element.style.margin = '0';
      element.style.backgroundColor = 'white';
      
      // Eliminar elementos que no queremos en la impresión
      const elementsToRemove = element.querySelectorAll('.print\\:hidden');
      elementsToRemove.forEach(el => el.parentNode?.removeChild(el));
      
      // Agregar marca de agua y fecha de generación
      const watermark = document.createElement('div');
      watermark.style.position = 'absolute';
      watermark.style.top = '50%';
      watermark.style.left = '50%';
      watermark.style.transform = 'translate(-50%, -50%) rotate(-30deg)';
      watermark.style.fontSize = '80px';
      watermark.style.color = 'rgba(200, 200, 200, 0.2)';
      watermark.style.fontWeight = 'bold';
      watermark.style.pointerEvents = 'none';
      watermark.style.zIndex = '0';
      watermark.innerText = 'DISOLGUA';
      element.style.position = 'relative';
      element.style.overflow = 'hidden';
      element.appendChild(watermark);
      
      // Generar la imagen
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
      });
      
      // Eliminar la copia del elemento
      document.body.removeChild(element);
      
      // Crear una ventana de impresión
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Por favor, permita las ventanas emergentes para imprimir');
        setIsGeneratingPDF(false);
        return;
      }
      
      // Configurar el contenido de la ventana de impresión
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Orden #${order?.id.slice(0, 8)} - DISOLGUA</title>
            <style>
              body {
                margin: 0;
                padding: 0;
                text-align: center;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              @media print {
                body {
                  margin: 0;
                  padding: 0;
                }
                img {
                  width: 100%;
                  max-height: 100vh;
                  object-fit: contain;
                  page-break-inside: avoid;
                }
              }
            </style>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() {
                    window.close();
                  }, 500);
                }, 300);
              };
            </script>
          </head>
          <body>
            <img src="${canvas.toDataURL('image/png')}" alt="Orden #${order?.id.slice(0, 8)}">
          </body>
        </html>
      `);
      printWindow.document.close();
      
    } catch (err) {
      console.error('Error al generar la impresión:', err);
      alert('Ocurrió un error al preparar la impresión. Por favor intente nuevamente.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;
    
    try {
      setIsGeneratingPDF(true);
      
      // Crear una copia del elemento para modificarlo sin afectar la UI
      const element = invoiceRef.current.cloneNode(true) as HTMLElement;
      document.body.appendChild(element);
      
      // Aplicar estilos específicos para el PDF
      element.style.width = '100%';
      element.style.padding = '0';
      element.style.margin = '0';
      element.style.backgroundColor = 'white';
      
      // Eliminar elementos que no queremos en el PDF
      const elementsToRemove = element.querySelectorAll('.print\\:hidden');
      elementsToRemove.forEach(el => el.parentNode?.removeChild(el));
      
      // Agregar marca de agua y fecha de generación
      const watermark = document.createElement('div');
      watermark.style.position = 'absolute';
      watermark.style.top = '50%';
      watermark.style.left = '50%';
      watermark.style.transform = 'translate(-50%, -50%) rotate(-30deg)';
      watermark.style.fontSize = '80px';
      watermark.style.color = 'rgba(200, 200, 200, 0.2)';
      watermark.style.fontWeight = 'bold';
      watermark.style.pointerEvents = 'none';
      watermark.style.zIndex = '0';
      watermark.innerText = 'DISOLGUA';
      element.style.position = 'relative';
      element.style.overflow = 'hidden';
      element.appendChild(watermark);
      
      // Generar el PDF
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
      });
      
      // Eliminar la copia del elemento
      document.body.removeChild(element);
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`orden-${order?.id.slice(0, 8)}.pdf`);
      
    } catch (err) {
      console.error('Error al generar el PDF:', err);
      alert('Ocurrió un error al generar el PDF. Por favor intente nuevamente.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!order) return;
    
    try {
      setIsGeneratingPDF(true);
      
      // Información general de la orden
      const orderInfo = [
        ['INFORMACIÓN GENERAL DE LA ORDEN', ''],
        ['', ''],
        ['Número de Orden', order.id.slice(0, 8)],
        ['Fecha de Creación', format(new Date(order.created_at), "d 'de' MMMM, yyyy - HH:mm", { locale: es })],
        ['Estado', statusLabels[order.status]],
        ['Estado de Pago', paymentStatusLabels[order.payment_status]],
        ['Método de Pago', 
          order.payment_method === 'card' ? 'Tarjeta' :
          order.payment_method === 'bankTransfer' ? 'Transferencia Bancaria' :
          order.payment_method === 'cash' ? 'Efectivo' :
          order.payment_method === 'twoCards' ? 'Dos Tarjetas' : order.payment_method
        ],
        ['Método de Entrega', order.delivery_method === 'delivery' ? 'Entrega a domicilio' : 'Recoger en tienda'],
        ['Total de la Orden', `Q${order.total_amount.toFixed(2)}`],
        ['', ''],
      ];

      // Información del cliente
      const clientInfo = [
        ['INFORMACIÓN DEL CLIENTE', ''],
        ['', ''],
        ['Nombre', order.shipping_address?.first_name || order.user.profile.name],
        ['Email', order.user.email],
        ['Teléfono', order.shipping_address?.phone || order.user.profile.phone || 'No especificado'],
        ['Empresa', order.shipping_address?.company || order.user.profile.company || 'No especificada'],
      ];

      if (order.shipping_address?.nit) {
        clientInfo.push([`${order.shipping_address.nit_type}`, order.shipping_address.nit]);
      }

      // Información de dirección de envío
      let addressInfo: string[][] = [];
      if (order.shipping_address) {
        addressInfo = [
          ['', ''],
          ['DIRECCIÓN DE ENVÍO', ''],
          ['', ''],
          ['Dirección', order.shipping_address.address],
          ['Detalles de Dirección', order.shipping_address.address_details || 'No especificados'],
          ['Ciudad', order.shipping_address.city],
          ['Región', order.shipping_address.region],
          ['Código Postal', order.shipping_address.postal_code || 'No especificado'],
        ];
      }

      // Información de productos
      const productsHeader = [
        ['', ''],
        ['DETALLE DE PRODUCTOS', '', '', '', '', ''],
        ['', ''],
        ['Producto', 'Código', 'Stock Disponible', 'Cantidad Solicitada', 'Precio Unitario', 'Subtotal']
      ];

      const productsData = orderItems.map(item => [
        item.product.name,
        item.product.code || 'N/A',
        item.product.stock || 0,
        item.quantity,
        `Q${item.price.toFixed(2)}`,
        `Q${(item.quantity * item.price).toFixed(2)}`
      ]);

      // Resumen financiero
      const financialSummary = [
        ['', ''],
        ['RESUMEN FINANCIERO', ''],
        ['', ''],
        ['Subtotal productos', `Q${(order.total_amount - order.shipping_cost).toFixed(2)}`],
        ['Envío', 'Se cobra al momento de la entrega'],
        ['Total', `Q${order.total_amount.toFixed(2)}`],
      ];

      // Combinar todos los datos
      const allData = [
        ...orderInfo,
        ...clientInfo,
        ...addressInfo,
        ...productsHeader,
        ...productsData,
        ...financialSummary
      ];

      // Crear el libro de trabajo
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(allData);

      // Configurar el ancho de las columnas
      const colWidths = [
        { wch: 25 }, // Columna A
        { wch: 20 }, // Columna B
        { wch: 15 }, // Columna C
        { wch: 15 }, // Columna D
        { wch: 15 }, // Columna E
        { wch: 15 }, // Columna F
      ];
      ws['!cols'] = colWidths;

      // Agregar estilos a las celdas principales (títulos)
      const titleCells = ['A1', 'A12', 'A20', 'A23'];
      titleCells.forEach(cell => {
        if (ws[cell]) {
          ws[cell].s = {
            font: { bold: true, sz: 14 },
            fill: { fgColor: { rgb: "E3F2FD" } },
            alignment: { horizontal: "center" }
          };
        }
      });

      // Estilo para los headers de la tabla de productos
      const productHeaderRow = 23 + (addressInfo.length > 0 ? addressInfo.length : 0);
      ['A', 'B', 'C', 'D', 'E', 'F'].forEach((col, index) => {
        const cell = `${col}${productHeaderRow + 3}`;
        if (ws[cell]) {
          ws[cell].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "F5F5F5" } },
            alignment: { horizontal: "center" }
          };
        }
      });

      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, 'Orden');

      // Crear una segunda hoja con análisis adicional si hay múltiples productos
      if (orderItems.length > 1) {
        const analysisData = [
          ['ANÁLISIS DE LA ORDEN', ''],
          ['', ''],
          ['Cantidad total de productos', orderItems.reduce((sum, item) => sum + item.quantity, 0)],
          ['Número de productos únicos', orderItems.length],
          ['Producto más caro', orderItems.reduce((max, item) => item.price > max.price ? item : max).product.name],
          ['Producto más económico', orderItems.reduce((min, item) => item.price < min.price ? item : min).product.name],
          ['Precio promedio por producto', `Q${(orderItems.reduce((sum, item) => sum + item.price, 0) / orderItems.length).toFixed(2)}`],
          ['', ''],
          ['DISTRIBUCIÓN POR PRODUCTOS', ''],
          ['', ''],
          ['Producto', 'Porcentaje del Total'],
          ...orderItems.map(item => [
            item.product.name,
            `${((item.price * item.quantity / order.total_amount) * 100).toFixed(1)}%`
          ])
        ];

        const analysisWs = XLSX.utils.aoa_to_sheet(analysisData);
        analysisWs['!cols'] = [{ wch: 30 }, { wch: 20 }];
        
        // Estilo para títulos en la hoja de análisis
        if (analysisWs['A1']) {
          analysisWs['A1'].s = {
            font: { bold: true, sz: 14 },
            fill: { fgColor: { rgb: "E8F5E8" } },
            alignment: { horizontal: "center" }
          };
        }
        if (analysisWs['A9']) {
          analysisWs['A9'].s = {
            font: { bold: true, sz: 12 },
            fill: { fgColor: { rgb: "FFF3E0" } },
            alignment: { horizontal: "center" }
          };
        }
        
        XLSX.utils.book_append_sheet(wb, analysisWs, 'Análisis');
      }

      // Generar y descargar el archivo
      const fileName = `orden-${order.id.slice(0, 8)}-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
    } catch (err) {
      console.error('Error al generar el archivo Excel:', err);
      alert('Ocurrió un error al generar el archivo Excel. Por favor intente nuevamente.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          <p className="mt-4 text-gray-600">Cargando detalles del pedido...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <p className="text-red-700 font-medium">Error</p>
          <p className="text-red-600">{error || 'No se encontró el pedido'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-red-600 hover:text-red-800 font-medium flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
        </div>
      </div>
    );
  }

  const StatusIcon = statusIcons[order.status];
  
  return (
    <div className="p-6 max-w-5xl mx-auto bg-white">
      {/* Imagen a pantalla completa */}
      {showFullImage && order.transfer_receipt_url && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFullImage(false)}
        >
          <div className="relative max-w-4xl w-full">
            <button 
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 rounded-full p-2 text-white"
              onClick={() => setShowFullImage(false)}
            >
              <XCircle className="w-6 h-6" />
            </button>
            <img 
              src={order.transfer_receipt_url} 
              alt="Comprobante de transferencia" 
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Acciones superiores (no imprimibles) */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 print:hidden">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800 font-medium flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a pedidos
          </button>
          
          {!isClientView && (
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${statusColors[order.status]}`}>
                <StatusIcon className="w-4 h-4" />
                {statusLabels[order.status]}
              </div>
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${paymentStatusColors[order.payment_status]}`}>
                <CreditCard className="w-4 h-4" />
                {paymentStatusLabels[order.payment_status]}
              </div>
            </div>
          )}
        </div>
        
        {/* Sección de pago pendiente para vista de cliente */}
        {isClientView && canRetryPayment(order.status, order.payment_status, order.payment_method) && (
          <div className={`mb-8 border rounded-xl p-6 ${
            order.payment_status === 'failed' 
              ? 'bg-red-50 border-red-200' 
              : 'bg-blue-50 border-blue-200'
          }`}>
            {/* Información específica para transferencias pendientes */}
            {order.payment_method === 'bankTransfer' && order.status === 'pending_payment' ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-yellow-600 mr-2" />
                  <h4 className="font-medium text-yellow-800">Transferencia en verificación</h4>
                </div>
                <p className="text-yellow-700 text-sm mt-2">
                  Hemos recibido tu comprobante de transferencia. Nuestro equipo está verificando el pago y te notificaremos cuando sea confirmado.
                </p>
                <div className="flex justify-end gap-3 mt-4">
                  {canCancelOrder(order.status) && (
                    <button
                      onClick={handleCancelOrder}
                      disabled={isUpdating}
                      className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                    >
                      {isUpdating ? 'Cancelando...' : 'Cancelar transferencia'}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start">
                <AlertCircle className={`w-6 h-6 mr-4 mt-1 ${
                  order.payment_status === 'failed' ? 'text-red-600' : 'text-blue-600'
                }`} />
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold ${
                    order.payment_status === 'failed' ? 'text-red-800' : 'text-blue-800'
                  }`}>
                    {order.payment_status === 'failed' ? 'Pago fallido' : 'Pendiente de pago'}
                  </h3>
                  <p className={`mt-2 ${
                    order.payment_status === 'failed' ? 'text-red-700' : 'text-blue-700'
                  }`}>
                    {getPaymentStatusMessage(order.status, order.payment_status, order.payment_method)}
                  </p>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handlePaymentRedirect}
                      className={`flex-1 py-3 px-6 rounded-lg font-medium transition-colors ${getPaymentButtonStyle(order.payment_status)}`}
                    >
                      <div className="flex items-center justify-center">
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        {getPaymentButtonText(order.payment_status)} - Q{order.total_amount.toFixed(2)}
                      </div>
                    </button>
                    {canCancelOrder(order.status) && (
                      <button
                        onClick={handleCancelOrder}
                        disabled={isUpdating}
                        className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                      >
                        {isUpdating ? 'Cancelando...' : 'Cancelar pedido'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          {!isClientView && order.status === 'pending' && (
            <button
              onClick={handleOrderConfirmation}
              disabled={isConfirming}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {isConfirming ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Confirmar Despacho
                </>
              )}
            </button>
          )}
          {/* Botón para confirmar pago de transferencia */}
          {!isClientView && order.status === 'pending_payment' && order.payment_status === 'pending' && (
            <button
              onClick={handleConfirmPayment}
              disabled={isConfirmingPayment}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {isConfirmingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Confirmar Pago
                </>
              )}
            </button>
          )}
          {!isClientView && order.status !== 'pending' && (
            <select
              value={order.status}
              onChange={(e) => updateOrderStatus(e.target.value as Order['status'])}
              disabled={isUpdating}
              className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
            >
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          )}
         
          <button 
            onClick={handlePrint}
            disabled={isGeneratingPDF}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isGeneratingPDF ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <Printer className="w-4 h-4" />
                Imprimir
              </>
            )}
          </button>
          
          {!isClientView && (
            <button 
              onClick={handleDownloadExcel}
              disabled={isGeneratingPDF}
              className="bg-green-100 hover:bg-green-200 text-green-700 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {isGeneratingPDF ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      {/* Panel de información rápida para administradores (no imprimible) */}
      {!isClientView && (
        <div className="mb-6 bg-blue-50 rounded-xl p-4 print:hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <p className="text-xs uppercase text-gray-500 font-medium">Cliente</p>
              <p className="font-medium truncate">{order.shipping_address?.first_name || order.user.profile.name}</p>
              <p className="text-sm text-gray-600 truncate">{order.user.email}</p>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <p className="text-xs uppercase text-gray-500 font-medium">Total</p>
              <p className="font-medium text-lg">Q{order.total_amount.toFixed(2)}</p>
              <p className="text-sm text-gray-600">{order.items.length} productos</p>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <p className="text-xs uppercase text-gray-500 font-medium">Fecha</p>
              <p className="font-medium">{format(new Date(order.created_at), "d MMM yyyy", { locale: es })}</p>
              <p className="text-sm text-gray-600">{format(new Date(order.created_at), "HH:mm", { locale: es })}</p>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <p className="text-xs uppercase text-gray-500 font-medium">Pago</p>
              <p className="font-medium text-sm">
                {order.payment_method === 'card' && 'Tarjeta'}
                {order.payment_method === 'bankTransfer' && 'Transferencia'}
                {order.payment_method === 'cash' && 'Efectivo'}
                {order.payment_method === 'twoCards' && 'Dos Tarjetas'}
              </p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${paymentStatusColors[order.payment_status]}`}>
                {paymentStatusLabels[order.payment_status]}
              </span>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <p className="text-xs uppercase text-gray-500 font-medium">Asignación</p>
              <p className="font-medium text-sm truncate">
                {order.assigned_to_name || 'Sin asignar'}
              </p>
              <div className="flex items-center gap-1 mt-1">
                <Flag className={`w-3 h-3 ${order.priority === 'urgent' ? 'text-red-500' : order.priority === 'high' ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className={`text-xs px-1.5 py-0.5 rounded ${priorityColors[order.priority || 'normal']}`}>
                  {priorityLabels[order.priority || 'normal']}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Documento de factura */}
      <div 
        ref={invoiceRef}
        className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 print:shadow-none print:border-0 print:m-0 print:p-0 print:w-full"
      >
        {/* Encabezado de factura */}
        <div className="bg-blue-50 border-b border-blue-100 p-8 print:bg-white print:p-4 print:border-b print:border-gray-300">
          <div className="flex flex-col md:flex-row justify-between items-start gap-6 print:flex-row print:gap-4">
            {/* Logo e información de la empresa */}
            <div className="space-y-2 print:w-1/2">
              <h1 className="text-2xl font-bold text-blue-900 print:text-black">DISOLGUA</h1>
              <p className="text-blue-700 print:text-black">Distribuidora del Sol Guatemala S.A.</p>
              <div className="text-sm text-gray-500 space-y-1 mt-2 print:mt-1 print:space-y-0.5">
                <p className="flex items-center gap-2 print:text-xs"><MapPin className="w-4 h-4 print:w-3 print:h-3" /> Guatemala, Guatemala</p>
                <p className="flex items-center gap-2 print:text-xs"><Phone className="w-4 h-4 print:w-3 print:h-3" /> +502 2238-0714</p>
                <p className="flex items-center gap-2 print:text-xs"><Mail className="w-4 h-4 print:w-3 print:h-3" /> info@disolgua.com</p>
              </div>
            </div>
            
            {/* Información del documento */}
            <div className="text-right bg-white border border-blue-100 rounded-xl p-4 shadow-sm print:border-none print:shadow-none print:p-0 print:w-1/2 print:flex print:flex-col print:items-end">
              <h2 className="text-xl font-bold text-gray-900 mb-1 print:text-lg">ORDEN</h2>
              <div className="flex flex-col gap-1 text-sm">
                <p className="flex items-center justify-end gap-2 font-medium print:text-xs">
                  <Hash className="w-4 h-4 text-blue-600 print:text-black print:w-3 print:h-3" /> 
                  No. {order.id.slice(0, 8)}
                </p>
                <p className="flex items-center justify-end gap-2 print:text-xs">
                  <Calendar className="w-4 h-4 text-blue-600 print:text-black print:w-3 print:h-3" /> 
                  {format(new Date(order.created_at), "d 'de' MMMM, yyyy", { locale: es })}
                </p>
                <div className="flex items-center justify-end gap-2 mt-2 print:mt-1">
                  <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${statusColors[order.status]} print:bg-transparent print:border-gray-400 print:text-black`}>
                    <StatusIcon className="w-3 h-3 print:text-black" />
                    {statusLabels[order.status]}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Información del cliente y detalles de compra */}
        <div className="p-8 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Información del cliente */}
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Despachado a
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-100">
                <p className="font-medium text-gray-900">{order.shipping_address?.first_name || order.user.profile.name}</p>
                {(order.shipping_address?.company || order.user.profile.company) && (
                  <p className="flex items-center gap-2 text-gray-700">
                    <Building className="w-4 h-4 text-gray-500" />
                    {order.shipping_address?.company || order.user.profile.company}
                  </p>
                )}
                {order.shipping_address?.nit && (
                  <p className="text-gray-700">
                    <span className="font-medium">{order.shipping_address.nit_type}: </span>
                    {order.shipping_address.nit}
                  </p>
                )}
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="w-4 h-4 text-gray-500" />
                  {order.shipping_address?.email || order.user.email}
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="w-4 h-4 text-gray-500" />
                  {order.shipping_address?.phone || order.user.profile.phone || 'No especificado'}
                </div>
              </div>
            </div>
            
            {/* Dirección de envío */}
            {order.shipping_address ? (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  Dirección de Envío
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-100">
                  <p className="font-medium text-gray-900">{order.shipping_address.address}</p>
                  {order.shipping_address.address_details && (
                    <p className="text-gray-700">{order.shipping_address.address_details}</p>
                  )}
                  <p className="text-gray-700">
                    {order.shipping_address.city}, {order.shipping_address.region}
                    {order.shipping_address.postal_code && ` (${order.shipping_address.postal_code})`}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 bg-yellow-50 p-4 rounded-lg">
                No hay información de dirección de envío disponible
              </div>
            )}
          </div>
        </div>
        
        {/* Productos */}
        <div className="p-8">
          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            Detalle de Productos
          </h3>
          
          {/* Tabla de productos */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-500">Producto</th>
                  {!isClientView && <th className="py-3 px-4 text-center text-sm font-medium text-gray-500">Stock</th>}
                  <th className="py-3 px-4 text-center text-sm font-medium text-gray-500">Código</th>
                  <th className="py-3 px-4 text-center text-sm font-medium text-gray-500">Cantidad</th>
                  <th className="py-3 px-4 text-right text-sm font-medium text-gray-500">Precio Unit.</th>
                  <th className="py-3 px-4 text-right text-sm font-medium text-gray-500">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orderItems.map((item) => (
                  <tr key={item.product_id} className="hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-md overflow-hidden border border-gray-200 flex-shrink-0">
                          <ProxyImage
                            src={item.product.main_image}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => e.currentTarget.src = '/placeholder-image.svg'}
                          />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{item.product.name}</p>
                        </div>
                      </div>
                    </td>
                    {!isClientView && (
                      <td className="py-4 px-4 text-center">
                        <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          (item.product.stock || 0) >= item.quantity
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {item.product.stock || 0}
                        </div>
                      </td>
                    )}
                    <td className="py-4 px-4 text-center">
                      <span className="font-mono text-sm">{item.product.code || 'N/A'}</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      {order.status === 'pending' && !isClientView ? (
                        <input
                          type="number"
                          min="0"
                          max={item.product.stock || item.quantity}
                          value={item.dispatch_quantity || 0}
                          onChange={(e) => handleDispatchQuantityChange(item.product_id, parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 text-center"
                        />
                      ) : (
                        <span className="font-medium">{item.quantity}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-right font-medium">Q{item.price.toFixed(2)}</td>
                    <td className="py-4 px-4 text-right font-medium">Q{(item.quantity * item.price).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Resumen */}
          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="flex flex-col items-end">
              <div className="w-full max-w-xs space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal productos</span>
                  <span className="font-medium">Q{(order.total_amount - order.shipping_cost).toFixed(2)}</span>
                </div>
                {order.status === 'pending' && !isClientView && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal a despachar</span>
                    <span className="font-medium">Q{calculateDispatchTotal().toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Envío</span>
                  <span className="font-medium text-gray-600">
                    Se cobra al momento de la entrega
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200 mt-2">
                  <span>Total</span>
                  <span className="text-blue-700">
                    {order.status === 'pending' && !isClientView
                      ? `Q${(calculateDispatchTotal() + order.shipping_cost).toFixed(2)}`
                      : `Q${order.total_amount.toFixed(2)}`
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer de la factura */}
        <div className="bg-gray-50 p-8 border-t border-gray-200 print:bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Notas adicionales */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Notas</h3>
              <p className="text-sm text-gray-500">
                Gracias por su compra. Para cualquier consulta relacionada con este pedido,
                por favor contacte a nuestro equipo de atención al cliente.
              </p>
            </div>
            
            {/* Resumen de estado */}
            <div className="md:text-right">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Estado del Pedido</h3>
              <div className="flex md:justify-end items-center gap-2">
                <StatusIcon className="w-4 h-4 text-gray-700" />
                <span className="text-sm text-gray-500">{statusLabels[order.status]}</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Última actualización: {format(new Date(), "d 'de' MMMM, yyyy", { locale: es })}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Panel de gestión administrativa */}
      {!isClientView && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 print:hidden">
          {/* Panel de asignación y prioridad */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-6 py-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-gray-500" />
                Gestión del Pedido
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Asignación de empleado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asignado a
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedEmployee}
                    onChange={(e) => handleAssignOrder(e.target.value)}
                    disabled={isAssigning}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sin asignar</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                  {isAssigning && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
                </div>
                {order.assigned_to_name && (
                  <p className="mt-1 text-sm text-gray-500">
                    Actualmente asignado a: <span className="font-medium">{order.assigned_to_name}</span>
                  </p>
                )}
              </div>

              {/* Prioridad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prioridad
                </label>
                <div className="flex gap-2">
                  {(['normal', 'high', 'urgent'] as const).map(priority => (
                    <button
                      key={priority}
                      onClick={() => handlePriorityChange(priority)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        order.priority === priority
                          ? priorityColors[priority] + ' ring-2 ring-offset-1 ring-gray-300'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Flag className={`w-3.5 h-3.5 ${priority === 'urgent' ? 'text-red-500' : priority === 'high' ? 'text-orange-500' : 'text-gray-400'}`} />
                      {priorityLabels[priority]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notas internas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agregar nota interna
                </label>
                <div className="flex gap-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Escribe una nota interna..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={2}
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleAddNote}
                    disabled={isAddingNote || !newNote.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isAddingNote ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Agregar nota
                  </button>
                </div>
              </div>

              {/* Notas existentes */}
              {order.internal_notes && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Última nota:</p>
                      <p className="text-sm text-yellow-700">{order.internal_notes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Panel de historial */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div
              className="border-b border-gray-100 px-6 py-4 flex items-center justify-between cursor-pointer"
              onClick={() => setShowHistoryPanel(!showHistoryPanel)}
            >
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <History className="w-5 h-5 text-gray-500" />
                Historial del Pedido
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  {orderHistory.length} eventos
                </span>
              </h3>
              <button className="text-gray-400 hover:text-gray-600">
                {showHistoryPanel ? '−' : '+'}
              </button>
            </div>
            {showHistoryPanel && (
              <div className="p-6 max-h-96 overflow-y-auto">
                {orderHistory.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No hay historial disponible
                  </p>
                ) : (
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    <div className="space-y-4">
                      {orderHistory.map((entry, index) => {
                        const ActionIcon = entry.action === 'status_change' ? Package :
                                          entry.action === 'assigned' ? UserCheck :
                                          entry.action === 'note_added' ? MessageSquare :
                                          entry.action === 'created' ? Clock : CheckCircle;
                        const iconColor = entry.action === 'status_change' ? 'bg-blue-100 text-blue-600' :
                                         entry.action === 'assigned' ? 'bg-purple-100 text-purple-600' :
                                         entry.action === 'note_added' ? 'bg-yellow-100 text-yellow-600' :
                                         'bg-green-100 text-green-600';

                        return (
                          <div key={entry.id} className="relative flex items-start gap-4">
                            <div className={`absolute left-0 top-0 flex items-center justify-center w-8 h-8 rounded-full border-2 border-white z-10 ${iconColor}`}>
                              <ActionIcon className="w-4 h-4" />
                            </div>
                            <div className="ml-12 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900 text-sm">
                                  {actionLabels[entry.action] || entry.action}
                                </h4>
                                {entry.new_status && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[entry.new_status] || 'bg-gray-100 text-gray-600'}`}>
                                    {statusLabels[entry.new_status] || entry.new_status}
                                  </span>
                                )}
                              </div>
                              {entry.previous_status && entry.new_status && (
                                <p className="text-xs text-gray-500">
                                  {statusLabels[entry.previous_status] || entry.previous_status} → {statusLabels[entry.new_status] || entry.new_status}
                                </p>
                              )}
                              {entry.notes && (
                                <p className="text-sm text-gray-600 mt-1 bg-gray-50 p-2 rounded">
                                  {entry.notes}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-xs text-gray-400">
                                  {format(new Date(entry.created_at), "d MMM yyyy, HH:mm", { locale: es })}
                                </p>
                                {entry.changed_by_name && (
                                  <span className="text-xs text-gray-400">
                                    • por {entry.changed_by_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Comprobante de transferencia (no imprimible) */}
      {order.payment_method === 'bankTransfer' && order.transfer_receipt_url && (
        <div className="mt-6 bg-white rounded-xl shadow-sm overflow-hidden print:hidden">
          <div className="border-b border-gray-100 px-6 py-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-500" />
              Comprobante de Transferencia
              {order.status === 'pending_payment' ? (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                  Pendiente de verificación
                </span>
              ) : (
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  Verificado
                </span>
              )}
            </h3>
          </div>
          <div className="p-6">
            <div className="relative bg-white border rounded-lg overflow-hidden">
              <img 
                src={order.transfer_receipt_url} 
                alt="Comprobante de transferencia"
                className="w-full h-auto max-h-[300px] object-contain"
                onError={(e) => {
                  console.error('Error al cargar la imagen:', e);
                  e.currentTarget.src = '/placeholder-image.svg';
                  e.currentTarget.classList.add('opacity-50');
                }}
              />
              <button 
                onClick={() => setShowFullImage(true)}
                className="absolute bottom-4 right-4 bg-white/90 hover:bg-white px-4 py-2 rounded-lg text-sm text-blue-600 hover:text-blue-800 transition-colors shadow-sm flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Ver imagen completa
              </button>
            </div>
          </div>
        </div>
      )}
      {order.payment_method === 'bankTransfer' && !order.transfer_receipt_url && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4 print:hidden">
          <h3 className="font-semibold text-yellow-800 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Transferencia sin comprobante
          </h3>
          <p className="text-yellow-700 text-sm mt-2">
            El método de pago es transferencia bancaria pero no hay URL de comprobante.
          </p>
        </div>
      )}
      
      {/* Mensaje de impresión (visible solo al imprimir) */}
      <div className="hidden print:block print:mb-4 print:text-center print:text-sm print:text-gray-500">
        <p>Este documento fue impreso el {format(new Date(), "d 'de' MMMM, yyyy", { locale: es })} y es una copia oficial de la orden.</p>
      </div>

      {/* Marca de agua para impresión */}
      <div className="hidden print:block print:fixed print:inset-0 print:flex print:items-center print:justify-center print:pointer-events-none print:opacity-10 print:rotate-[-30deg] print:z-0">
        <p className="text-[8rem] font-bold text-gray-400 select-none">DISOLGUA</p>
      </div>
      
      {/* Estilos para impresión */}
      <style>
        {`
          @media print {
            body {
              background-color: white;
              color: black;
              font-size: 12pt;
              margin: 0;
              padding: 0;
            }
            
            .print\\:hidden {
              display: none !important;
            }
            
            .print\\:shadow-none {
              box-shadow: none !important;
            }
            
            .print\\:border-0 {
              border: none !important;
            }
            
            .print\\:bg-white {
              background-color: white !important;
            }
            
            /* Ajustes específicos para tablas en impresión */
            table { 
              width: 100%;
              border-collapse: collapse;
            }
            
            th {
              background-color: #f8f9fa !important;
              color: #333 !important;
              font-weight: bold;
              border-bottom: 1px solid #ddd;
              text-align: left;
              padding: 8px;
              font-size: 14pt !important;
            }
            
            td {
              padding: 8px;
              border-bottom: 1px solid #eee;
              font-size: 13pt !important;
            }
            
            /* Detalles de productos */
            [class*="p-8"] h3:has(+ .overflow-x-auto) {
              font-size: 16pt !important;
              margin-bottom: 12px !important;
            }
            
            /* Mejora para productos */
            td .font-medium {
              font-size: 13pt !important;
              font-weight: 600 !important;
            }
            
            /* Ajustes para la imagen del producto */
            td .w-12.h-12 {
              width: 3rem !important;
              height: 3rem !important;
            }
            
            /* Asegurar que el subtotal y precio sea más legible */
            td:nth-last-child(-n+2) {
              font-size: 13pt !important;
              font-weight: 600 !important;
            }
            
            /* Totales más grandes */
            .border-t .text-lg {
              font-size: 15pt !important;
            }
            
            /* Evitar cortes en elementos importantes */
            tr { page-break-inside: avoid; }
            img { max-width: 100%; height: auto; }
            
            /* Añadir saltos de página donde sea necesario */
            .print\\:break-before-page { page-break-before: always; }
            .print\\:break-after-page { page-break-after: always; }
            
            /* Asegurar que los iconos sean visibles */
            svg {
              fill: currentColor;
              display: inline-block;
            }
            
            /* Mejorar el aspecto de los contenedores */
            .bg-blue-50, .bg-gray-50 {
              background-color: white !important;
            }
            
            /* Ajustar los márgenes y el padding */
            .p-8 {
              padding: 1.5rem !important;
            }
            
            .rounded-xl, .rounded-lg {
              border-radius: 0 !important;
            }
          }
        `}
      </style>
    </div>
  );
} 